import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Container, Text } from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { useTranslation } from "react-i18next";
import { useServerApi } from "../../hooks/userServerApi";
import { useTabNavigate } from "../../hooks/useTabNavigate";
import PageListHeader from "../../components/pageListHeader";
import AccountingReportFilters from "../../components/accounting/AccountingReportFilters";
import TrialBalanceView from "../../components/accounting/TrialBalanceView";
import { downloadTrialBalanceCsv } from "../../components/accounting/trialBalanceUtils";

const TrialBalance = () => {
  const { t } = useTranslation();
  const [api] = useServerApi();
  const tabNav = useTabNavigate();
  const [report, setReport] = useState(null);
  const [fiscalPeriodId, setFiscalPeriodId] = useState(null);
  const [periodName, setPeriodName] = useState("");
  const [asOfDate, setAsOfDate] = useState(null);
  const [accountTypes, setAccountTypes] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const loadReport = async () => {
    setLoading(true);
    try {
      const query = {};
      if (fiscalPeriodId) query.fiscalPeriodId = fiscalPeriodId;
      if (asOfDate) query.endDate = asOfDate.toISOString();
      const data = await api.Accounting.getTrialBalance(query);
      setReport(data);
      setLoaded(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!fiscalPeriodId) return;
    const loadPeriodName = async () => {
      try {
        const period = await api.getById({
          apiEntity: "fiscalPeriod",
          id: fiscalPeriodId,
        });
        setPeriodName(period?.name || "");
      } catch (error) {
        setPeriodName("");
      }
    };
    loadPeriodName();
    loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fiscalPeriodId]);

  const handleDownload = useCallback(() => {
    if (!report || filteredRows.length === 0) {
      showNotification({
        color: "yellow",
        title: t("accounting.trialBalance.downloadFailed"),
        message: t("accounting.trialBalance.noActivity"),
      });
      return;
    }
    downloadTrialBalanceCsv({
      rows: filteredRows,
      report,
      periodName,
      asOfDate,
      t,
    });
  }, [asOfDate, filteredRows, periodName, report, t]);

  const canDownload = Boolean(report && filteredRows.length > 0);

  const handleFilteredRowsChange = useCallback((rows) => {
    setFilteredRows(rows);
  }, []);

  const actionButtons = useMemo(
    () => [
      {
        label: t("Download"),
        variant: "default",
        disabled: !canDownload,
        onClick: handleDownload,
      },
      {
        label: t("Refresh"),
        variant: "default",
        loading,
        onClick: loadReport,
      },
      {
        label: t("Close"),
        variant: "default",
        onClick: () => tabNav.closeActive(),
      },
    ],
    [canDownload, handleDownload, loading, t, tabNav]
  );

  return (
    <Container size="xl">
      <PageListHeader
        title={t("accounting.modules.trialBalance.label")}
        actionButtons={actionButtons}
      />
      <Text size="sm" color="dimmed" mb="md">
        {t("accounting.trialBalance.pageIntro")}
      </Text>
      <AccountingReportFilters
        fiscalPeriodId={fiscalPeriodId}
        onFiscalPeriodChange={setFiscalPeriodId}
        asOfDate={asOfDate}
        onAsOfDateChange={setAsOfDate}
        showAsOfDate
        showAccountTypeFilter
        accountTypes={accountTypes}
        onAccountTypesChange={setAccountTypes}
      />
      {!loaded && !loading && (
        <Text size="sm" color="dimmed" mb="md">
          {t("accounting.trialBalance.selectPeriodHint")}
        </Text>
      )}
      <TrialBalanceView
        report={report}
        loading={loading}
        accountTypes={accountTypes}
        onFilteredRowsChange={handleFilteredRowsChange}
      />
    </Container>
  );
};

export default TrialBalance;
